/**
 * PortalMobileLayout — Mobile-First Layout für MA-Portal.
 *
 * Features:
 * - Sticky Header (56px): Logo, Seitenname, Notification Bell mit Badge
 * - Content Area (flex-1, overflow-y-auto, pb-20)
 * - Bottom Navigation (64px + safe-area-inset-bottom)
 *   - Home, Zeiten, Abwesend, Dokumente, Profil
 * - 100dvh, safe-area support, Dark Mode
 *
 * @layout PortalMobileLayout
 * @since v2.243.0
 */
import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';

const API = import.meta.env.VITE_API_URL || '';

// ─── Icons (inline SVG) ───
const IconHome = ({ active }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill={active ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={active ? '0' : '2'} strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />{!active && <polyline points="9 22 9 12 15 12 15 22" />}
  </svg>
);
const IconClock = ({ active }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={active ? '2.5' : '2'} strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
  </svg>
);
const IconCalendarOff = ({ active }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={active ? '2.5' : '2'} strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
    {active && <><line x1="10" y1="14" x2="14" y2="18" /><line x1="14" y1="14" x2="10" y2="18" /></>}
  </svg>
);
const IconFileText = ({ active }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={active ? '2.5' : '2'} strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" />
  </svg>
);
const IconUser = ({ active }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={active ? '2.5' : '2'} strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" />
  </svg>
);
const IconBell = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 01-3.46 0" />
  </svg>
);

const NAV_ITEMS = [
  { path: '/portal', key: 'home', Icon: IconHome, labelKey: 'mobilePortal.nav.home' },
  { path: '/portal/zeiten', key: 'zeiten', Icon: IconClock, labelKey: 'mobilePortal.nav.zeiten' },
  { path: '/portal/abwesend', key: 'abwesend', Icon: IconCalendarOff, labelKey: 'mobilePortal.nav.abwesend' },
  { path: '/portal/dokumente', key: 'dokumente', Icon: IconFileText, labelKey: 'mobilePortal.nav.dokumente' },
  { path: '/portal/profil', key: 'profil', Icon: IconUser, labelKey: 'mobilePortal.nav.profil' },
];

// Page titles by path
const PAGE_TITLES = {
  '/portal': 'mobilePortal.titles.home',
  '/portal/zeiten': 'mobilePortal.titles.zeiten',
  '/portal/abwesend': 'mobilePortal.titles.abwesend',
  '/portal/dokumente': 'mobilePortal.titles.dokumente',
  '/portal/profil': 'mobilePortal.titles.profil',
  '/portal/notifications': 'mobilePortal.titles.notifications',
};

export default function PortalMobileLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { theme } = useTheme();
  const [ungelesen, setUngelesen] = useState(0);

  // Fetch notification count
  useEffect(() => {
    const fetchCount = () => {
      fetch(`${API}/api/portal/notifications?limit=1`, { credentials: 'include' })
        .then(r => r.json())
        .then(d => { if (typeof d.ungelesen === 'number') setUngelesen(d.ungelesen); })
        .catch(() => {});
    };
    fetchCount();
    const iv = setInterval(fetchCount, 60000);
    return () => clearInterval(iv);
  }, []);

  const pageTitle = PAGE_TITLES[location.pathname] || 'mobilePortal.titles.home';
  const activeKey = NAV_ITEMS.find(n => n.path === location.pathname)?.key
    || NAV_ITEMS.find(n => location.pathname.startsWith(n.path + '/'))?.key
    || 'home';

  return (
    <div
      className="flex flex-col bg-[var(--bg-page,#F8F9FC)] text-[var(--text-primary,#1A1A2E)]"
      style={{ height: '100dvh', touchAction: 'manipulation' }}
    >
      {/* ─── Header ─── */}
      <header className="flex items-center justify-between px-4 bg-[var(--bg-card,#fff)] border-b border-[var(--border-card,#E5E7EB)] flex-shrink-0" style={{ height: 56 }}>
        <button
          type="button"
          onClick={() => navigate('/portal')}
          aria-label="WeShip Home"
          className="flex items-center cursor-pointer active:opacity-70 transition-opacity -ml-1 p-1"
        >
          <img
            src={theme === 'dark' ? '/weship-logo-light.png' : '/weship-logo.png'}
            alt="WeShip"
            className="h-7"
          />
        </button>
        <span className="text-base font-semibold text-[var(--text-primary,#1A1A2E)]">
          {t(pageTitle, pageTitle.split('.').pop())}
        </span>
        <button
          onClick={() => navigate('/portal/notifications')}
          className="relative w-10 h-10 flex items-center justify-center rounded-xl text-[var(--text-secondary,#4B5563)] hover:bg-[var(--bg-inset,#F8F9FC)] transition-colors"
        >
          <IconBell />
          {ungelesen > 0 && (
            <span className="absolute top-1 right-1 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1">
              {ungelesen > 99 ? '99+' : ungelesen}
            </span>
          )}
        </button>
      </header>

      {/* ─── Content ─── */}
      <main className="flex-1 overflow-y-auto pb-20">
        {children}
      </main>

      {/* ─── Bottom Navigation ─── */}
      <nav
        className="fixed bottom-0 left-0 right-0 bg-[var(--bg-card,#fff)] border-t border-[var(--border-card,#E5E7EB)] flex items-center justify-around"
        style={{
          height: 64,
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
        {NAV_ITEMS.map(({ path, key, Icon, labelKey }) => {
          const isActive = key === activeKey;
          return (
            <button
              key={key}
              onClick={() => navigate(path)}
              className={`flex flex-col items-center justify-center gap-0.5 flex-1 h-full transition-colors ${
                isActive
                  ? 'text-[#338edc]'
                  : 'text-[var(--text-tertiary,#6B7280)]'
              }`}
            >
              <Icon active={isActive} />
              <span className={`text-[10px] font-medium ${isActive ? 'text-[#338edc]' : 'text-[var(--text-tertiary,#6B7280)]'}`}>
                {t(labelKey, key.charAt(0).toUpperCase() + key.slice(1))}
              </span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}
